<template>
    <header>
        <!-- Standard navbar otherwise -->
        <!-- Marks Promo's own chrome for the platform e2e suite, which asserts none of it renders
             inside the One Retail shell. Standalone only: App.vue drops this whole component when
             embedded. -->
        <v-app-bar
            data-rtls-product-chrome
            app
            class="promo-toolbar elevation-3"
            :class="{ warning: notUsingLocalDatabase }"
        >
            <v-toolbar-items variant="flat" class="toolbar-items">
                <v-btn exact text :to="defaultRoute" active-class="no-active">
                    <!-- make the (optional) client logo invisible until loaded -->
                    <v-img
                        id="client-logo"
                        src="/organisation-assets/logo.png"
                        @load="logoLoaded"
                    />
                    <v-img :src="logo" width="185px" />
                </v-btn>
            </v-toolbar-items>

            <v-spacer />

            <v-toolbar-items
                v-for="(route, index) in routes"
                :key="index"
                class="toolbar-items"
                :class="route.cssClasses"
            >
                <feature-toggle :toggle="route.meta.canAccess" slot-class="v-toolbar__items">
                    <template v-slot:default>
                        <v-btn
                            :key="index"
                            exact
                            text
                            :to="route.path"
                            class="text-none nav-link"
                            :class="
                                $route.fullPath.indexOf(route.path) === 0
                                    ? 'active-link font-weight-bold'
                                    : ''
                            "
                        >
                            {{ toSentenceCase($t(route.linkText)) }}
                        </v-btn>
                    </template>
                </feature-toggle>
            </v-toolbar-items>

            <v-spacer />

            <v-toolbar-items class="toolbar-items">
                <div v-if="!hideWeeks" class="date">
                    <span class="week-of-year font-weight-bold">
                        {{ weekNumberClient(weekOfYear) }}
                    </span>
                    <span>{{ fullDateText(date) }}</span>
                </div>
                <!--Was commented out according PROWEB-1359-->
                <!--<icon icon-name="cog" class="cog" small />-->
            </v-toolbar-items>

            <v-toolbar-items class="toolbar-items">
                <v-divider class="vertical-divider" vertical />
            </v-toolbar-items>

            <feature-toggle :toggle="canAccessAdminAreaOrClientSystemsTesting">
                <v-toolbar-items class="toolbar-items">
                    <v-btn text exact depressed @click="toAdminPage">
                        <span><v-icon v-once color="primary">mdi-cog</v-icon></span>
                    </v-btn>
                </v-toolbar-items>
            </feature-toggle>

            <v-toolbar-items>
                <v-menu offset-y :close-on-content-click="false">
                    <template v-slot:activator="{ props }">
                        <v-btn
                            v-bind="props"
                            text
                            class="user-menu"
                            @click="isExpanded = !isExpanded"
                        >
                            <v-avatar class="avatar" color="primary" size="40">
                                <span class="white--text headline">{{ userProfile.initials }}</span>
                            </v-avatar>
                            <span class="name-text">{{ userProfile.fullName }}</span>
                            <main-expand-button :is-expanded="isExpanded" />
                        </v-btn>
                    </template>

                    <v-list>
                        <v-list-item v-if="isMultitenancyEnabled">
                            <v-list-item-title>
                                <tenant-selector />
                            </v-list-item-title>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-title>
                                <v-btn
                                    id="navbar-logout"
                                    variant="text"
                                    class="text-none nav-link"
                                    @click="onLogout"
                                >
                                    {{ toSentenceCase($t('actions.logout')) }}
                                </v-btn>
                            </v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="shouldDisplayUserDocsLink">
                            <v-list-item-title>
                                <v-btn
                                    id="navbar-user-docs"
                                    class="text-none nav-link"
                                    text
                                    href="/docs/"
                                    target="_blank"
                                >
                                    {{ toSentenceCase($t('actions.viewUserDocs')) }}
                                </v-btn>
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-toolbar-items>
        </v-app-bar>
    </header>
</template>

<script>
import { get } from 'lodash';
import { mapState, mapGetters, mapActions } from 'vuex';
import logo from '../../img/retail-promotions-logo.png';
import featureFlags from '@enums/feature-flags';
import UXEvents from '@enums/ux-events';
import defaultRouteMixin from '../pages/mixins/default-route-mixin';
import navigation from '@/js/navigation';

export default {
    mixins: [defaultRouteMixin],
    localizationKey: 'nested',
    data() {
        return {
            logo,
            date: new Date(),
            isExpanded: false,
            canAccessAdminAreaOrClientSystemsTesting:
                featureFlags.canAccessAdminAreaOrClientSystemsTesting,
            blurOnWindowFocus: null,
        };
    },
    computed: {
        ...mapGetters('weeks', ['getWeekByDate']),
        ...mapGetters('context', ['isMultitenancyEnabled']),
        ...mapState('context', ['profile', 'tenants']),
        ...mapState('clientConfig', ['toggleLogic', 'mainNavbarRoutes', 'notUsingLocalDatabase']),
        routes() {
            return this.$router.options.routes
                .filter(route => !route.omitFromNav)
                .map(route => ({ ...route, index: this.mainNavbarRoutes[route.id].position }))
                .sort((a, b) => {
                    if (Number.isFinite(a.index) && Number.isFinite(b.index)) {
                        return a.index - b.index;
                    }
                    if (Number.isFinite(a.index)) return -1;
                    if (Number.isFinite(b.index)) return 1;
                    return (b.name || '').localeCompare(a.name || '');
                });
        },
        hideWeeks() {
            return this.toggleLogic[featureFlags.reportingOnly];
        },
        weekOfYear() {
            return get(this.getWeekByDate(this.date, this.$moment), 'weekOfYear');
        },
        userProfile() {
            if (this.profile) {
                const { firstname, surname } = this.profile;
                return {
                    fullName: `${firstname} ${surname}`,
                    initials: this.toSentenceCase(`${firstname[0]}${surname[0]}`),
                };
            }
            return {
                fullName: '',
                initials: '',
            };
        },

        shouldDisplayUserDocsLink() {
            return this.toggleLogic[featureFlags.canAccessUserDocs];
        },
    },
    mounted() {
        this.blurOnWindowFocus = () => {
            if (this.$el?.contains(document.activeElement)) {
                document.activeElement.blur();
            }
        };
        window.addEventListener('focus', this.blurOnWindowFocus);
    },
    beforeUnmount() {
        window.removeEventListener('focus', this.blurOnWindowFocus);
    },
    methods: {
        ...mapActions('context', ['logout']),

        logoLoaded() {
            Object.assign(document.getElementById('client-logo').style, {
                maxWidth: '2.5rem',
                minWidth: '2.5rem',
                visibility: 'visible',
                marginRight: '1rem',
            });
        },
        toAdminPage() {
            navigation.toAdminPage({ router: this.$router });
        },

        onLogout() {
            return this.logout().then(
                redirectUrl => {
                    this.$router.push(redirectUrl);
                    this.emitter.emit(UXEvents.logout);
                },
                () => {
                    // eslint-disable-next-line no-console
                    console.log('Logout error!');
                }
            );
        },
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_mixins.scss';
@import '@style/base/_variables.scss';
.v-toolbar__items {
    display: flex;
    height: inherit;

    & > .v-btn {
        border-radius: 0;
        height: 100% !important;
        max-height: none;
    }
}
.v-toolbar-items {
    > .v-btn {
        border-radius: 0;
        height: 100% !important;
        max-height: none;
    }
}
.promo-toolbar.v-app-bar--fixed {
    background-color: $promo-white;
    z-index: $main-navbar-z-index;
}

.promo-toolbar.v-app-bar--fixed.warning {
    background-color: $promo-orange-dark !important;
}

.vertical-divider {
    height: inherit;
    margin: 0 1.4rem;
}

.toolbar-items {
    align-items: center;
}

.nav-link {
    color: $promo-primary-colour !important;
    padding-bottom: 0.5rem !important;
    border-radius: 0 !important;
    background-color: transparent !important;

    :deep(.v-btn__content) {
        font-size: 1.4rem;
    }

    // Needed to override the style applied by Vuetify which displays a background colour on the link.
    &::before {
        opacity: 0;
    }
}

.admin-toolbar-item {
    background-color: $promo-grey-blue-tint;
}

.active-link {
    border-bottom: 0.5rem solid $promo-primary-colour;
    padding-bottom: 0 !important;
}

.date {
    color: $text-colour;
    font-size: 1.2rem;

    .week-of-year {
        margin-right: 0.6rem;
    }
}

.avatar {
    margin-right: 1rem;
}

.name-text {
    color: $text-colour;
    font-size: 1.4rem;
}

:deep(.cog) {
    margin-left: 5.8rem;
}

.user-menu {
    box-shadow: none;
}

.v-menu__content--fixed {
    border-radius: 0;
}

.v-list.v-theme--light.v-list--one-line {
    border-radius: 0;
}

.v-list-item--density-default.v-list-item--one-line {
    padding-top: 0;
    padding-bottom: 0;
}

:deep(.v-list-item__content) {
    font-size: 1rem;
    padding: 0;
}

.v-menu .v-btn--size-default {
    font-size: 1rem;
    height: 2.4rem !important;
    box-shadow: none;
}

:deep(.v-toolbar__content) {
    padding: 0 1.6rem;

    & .v-toolbar-items > .v-btn:not(.user-menu) {
        background-color: #fff !important;

        &::before {
            opacity: 0 !important;
        }

        .v-btn__overlay,
        .v-btn__underlay {
            opacity: 0 !important;
            background: #fff !important;
        }
    }

    .v-btn--active:not(.user-menu) > .v-btn__overlay {
        opacity: 0 !important;
        background: #fff !important;
    }

    .v-btn--active:not(.user-menu)::before {
        opacity: 0 !important;
        background: #fff !important;
    }

    .mdi-cog {
        font-size: 2.4rem;
    }

    .expand-btn .v-icon {
        font-size: 1.2rem;
        padding-left: 0.4rem;
    }
}

// This is a workaround to remove the styling applied by the --active class when the logo is clicked.
// See here for more details: https://github.com/vuetifyjs/vuetify/issues/8172
.v-btn--active.no-active::before {
    opacity: 0 !important;
}
</style>
